import scrollIntoView from 'scroll-into-view';
import { useFragment, graphql } from 'react-relay';
import { flattenConnectionNodes } from '../../../utils/relayUtils';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext';
import { FC, useCallback, useRef, RefObject, MouseEventHandler, memo, MouseEvent } from 'react';
import {
    trackItemClick,
    trackItemsDisplayed,
    trackItemImpressions,
} from './pdpRespUnavailableHeaderYMALTracking';

// components
import { Link } from 'dibs-elements/exports/Link';
import { FormattedMessage } from 'dibs-react-intl';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { PdpRespUnavailableHeaderYMALItem } from './PdpRespUnavailableHeaderYMALItem';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';

// styles
import styles from './PdpRespUnavailableHeaderYMAL.scss';

// types
import { PdpRespUnavailableHeaderYMAL_viewer$key as Viewer } from './__generated__/PdpRespUnavailableHeaderYMAL_viewer.graphql';

const viewerFragment = graphql`
    fragment PdpRespUnavailableHeaderYMAL_viewer on Viewer {
        topYMAL: moreLikeThisItem(first: 12, itemId: $itemId, onlyAuctionItems: true)
            @skip(if: $afterDisplay) {
            edges {
                node {
                    title
                    serviceId
                    browseUrl
                    categoryCode
                    ecommerceTrackingParams
                    ...PdpRespUnavailableHeaderYMALItem_item
                }
            }
        }
    }
`;

export const PdpRespUnavailableHeaderYMAL: FC<{
    viewer: Viewer;
    scrollToRef?: RefObject<HTMLDivElement>;
}> = memo(
    ({ scrollToRef, viewer: viewerRef }) => {
        const viewer = useFragment(viewerFragment, viewerRef);
        const { isTablet } = useServerVarsContext();
        const containerRef = useRef<HTMLDivElement>(null);
        const ymalItems = flattenConnectionNodes(viewer?.topYMAL);
        const itemsToShow = isTablet ? ymalItems.slice(0, 10) : ymalItems;

        const onViewMoreClick: MouseEventHandler = useCallback(() => {
            const elementRef = scrollToRef?.current;
            if (elementRef) {
                scrollIntoView(elementRef, { time: 500, align: { top: 0, topOffset: 100 } }, () =>
                    elementRef.focus()
                );
            }
        }, [scrollToRef]);

        const onItemClick = useCallback(
            (index: number, event: MouseEvent) => {
                const item = itemsToShow?.[index];
                if (item) {
                    trackItemClick(item, index, event);
                }
            },
            [itemsToShow]
        );

        const onVisibilityChange = useCallback(
            ({ isVisible }: { isVisible: boolean }) => {
                if (isVisible) {
                    trackItemsDisplayed();
                    trackItemImpressions(itemsToShow);
                }
            },
            [itemsToShow]
        );

        return (
            <div ref={containerRef} data-tn="pdp-view-similar-items">
                <VisibilityTracker
                    elementRef={containerRef}
                    onVisibilityChange={onVisibilityChange}
                />
                <div className={styles.headerContainer} data-tn="view-similar-items-header">
                    <HeadingLevel>
                        {Heading => (
                            <Heading className={styles.header}>
                                <FormattedMessage
                                    id="PdpRespUnavailableHeaderYMAL.viewSimilarItems"
                                    defaultMessage="View Similar Items"
                                />
                            </Heading>
                        )}
                    </HeadingLevel>
                    {scrollToRef && (
                        <Link
                            className={styles.viewMore}
                            onClick={onViewMoreClick}
                            dataTn="view-more-similar-items"
                        >
                            <FormattedMessage
                                id="PdpRespUnavailableHeaderYMAL.viewMore"
                                defaultMessage="View More"
                            />
                        </Link>
                    )}
                </div>

                <div className={styles.itemsContainer}>
                    {itemsToShow.map((item, index) => (
                        <PdpRespUnavailableHeaderYMALItem
                            item={item}
                            index={index}
                            key={item?.serviceId}
                            onClick={onItemClick}
                        />
                    ))}
                </div>
            </div>
        );
    },
    () => true
);
